import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  AfterViewInit,
} from "@angular/core";
import { FormBuilder, FormGroup, FormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { GetTaskSwimLanesDto } from "../../../core/models/task-management/getTaskSwimLanesDto";
import { LoaderService } from "../../../core/services/loader.service";
import { TaskManagementService } from "../../../core/services/task-management.service";
import { EnumSalesTaskStatus } from "../../../shared/enums/salesTaskStatus";
import { TaskManagerView } from "../../../shared/enums/taskManagerView";
import { TaskTypeEnum } from "../../../shared/enums/taskTypeEnum";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { TabsModule } from "primeng/tabs";
import { CommonModule } from "@angular/common";
import { CheckboxModule } from "primeng/checkbox";
import { RadioButtonModule } from "primeng/radiobutton";
import { ButtonModule } from "primeng/button";
import { GetTaskDefaultViewSettingDto } from "../../../core/models/task-management/getTaskDefaultViewSetting";

@UntilDestroy()
@Component({
  selector: "jni-task-global-setting",
  templateUrl: "./task-global-setting.component.html",
  styleUrls: ["./task-global-setting.component.scss"],
  imports: [
    TabsModule,
    CommonModule,
    CheckboxModule,
    RadioButtonModule,
    FormsModule,
    ButtonModule,
  ],
})
export class TaskGlobalSettingComponent implements OnInit, AfterViewInit {
  swimLanes: GetTaskSwimLanesDto[] = [];
  restSwimLanes: { statusId: number; isActive: boolean }[] = [];
  taskType = 0;
  taskTypeSales: TaskTypeEnum = TaskTypeEnum.Sales;
  @Output() hide = new EventEmitter();
  @Output() changeTaskType = new EventEmitter<{
    type: number;
    viewType: { name: string; id: number };
    duration?: string;
  }>();
  swimLanesForm!: FormGroup;
  viewType: number = TaskManagerView.List;
  draggedStatus!: GetTaskSwimLanesDto | null;
  draggedIndex!: number;
  TaskManagerView = TaskManagerView;
  TaskTypeEnum = TaskTypeEnum;
  workflowStatusList!: Record<number, string>;
  swimLanesUpdate = false;
  durationTypes: string[] = ["Monthly", "Weekly", "Daily"];
  selectedDuration = "Monthly";

  taskStatusKeyPairsSales: Record<number, string> = {
    [EnumSalesTaskStatus.New]: "New",
    [EnumSalesTaskStatus.Blocked]: "Blocked",
    [EnumSalesTaskStatus.InProgress]: "In Progress",
    [EnumSalesTaskStatus.InReview]: "In Review",
    [EnumSalesTaskStatus.Closed]: "Closed",
  };
  constructor(
    private _taskManagementService: TaskManagementService,
    private _toastr: JniToastrService,
    private _loaderService: LoaderService,
    private _formBuilder: FormBuilder,
    private _workflowService: ProjectWorkflowService
  ) {
    this.swimLanesForm = this._formBuilder.group({
      status: [[]],
      taskType: [1],
    });
  }

  ngOnInit(): void {
    this.getWorkflowStatusList();
    this.getDefaultView();
  }

  ngAfterViewInit() {
    const swimInit = this.taskType === 0 ? this.taskTypeSales : this.taskType;
    this.getSwimLanes(swimInit);
  }

  getWorkflowStatusList() {
    this._workflowService.getWorkflowStatusList().subscribe({
      next: res => {
        this.workflowStatusList = {};
        const statusEnums: Record<number, string> = {};
        if (res?.data) {
          res.data.forEach((status: { status: string; id: number }) => {
            if (status.id !== 9 && status.id !== 1) {
              statusEnums[status.id] = status.status;
            }
          });
          this.workflowStatusList = statusEnums;
        }
      },
      error: () => {
        this._toastr.error("Error", "Unable to get Workflow Status List");
      },
    });
  }

  getSwimLanes(taskType: number) {
    this._taskManagementService
      .getSwimLanes()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.swimLanes = [];
          if (res.data != null && res.data.length > 0) {
            res.data.forEach(task => {
              if (Number(taskType) === Number(task.taskType)) {
                if (Number(taskType) === Number(TaskTypeEnum.Sales)) {
                  task.name = this.taskStatusKeyPairsSales[task.status];
                  this.swimLanes!.push(task);
                } else {
                  if (this.workflowStatusList[task.status]) {
                    task.name = this.workflowStatusList[task.status];
                    this.swimLanes!.push(task);
                  }
                }
              }
            });
          }
        },
        error: () => {
          this._toastr.error("Error", "Error Fetching Swim lanes");
        },
      });
  }

  checkType(taskType: number, type: TaskTypeEnum) {
    return taskType === type;
  }

  createOrUpdateSwimLanes() {
    this._loaderService.setLoaderState(true);
    const taskTypeInit =
      this.taskType === 0 ? this.taskTypeSales : this.taskType;
    const payload = this.swimLanesForm.getRawValue();
    payload.status = this.restSwimLanes;
    payload.taskType = taskTypeInit;

    this._taskManagementService
      .addorUpdateSwimLanes(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.data != null) {
            this._loaderService.setLoaderState(false);
            this._toastr.success("Success", "Setting updated successfully!");
            this.getSwimLanes(taskTypeInit);
          } else {
            this._toastr.error("Error", res.message || "Error adding client");
          }
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message || "Error adding client");
        },
      });
  }

  onChangeViewType(type: number) {
    this.viewType = type;
  }

  onChangeTaskType(type: number) {
    this.taskType = type;
    this.getSwimLanes(type);
  }

  getDefaultView() {
    this._taskManagementService
      .getDefaultViewSetting()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.swimLanes = [];
          if (res.data != null) {
            this.viewType = res.data?.viewType ?? 1; //As per the ticket #13982 comments
            this.selectedDuration = res?.data?.durationType ?? "";
            this.setByDefaultTasksViews();
          } else {
            this.viewType = 1;
            this.selectedDuration = "";
            this.setByDefaultTasksViews();
          }
        },
        error: () => {
          this._toastr.error("Error", "Error Fetching Task Default View!");
        },
      });
  }

  setByDefaultTasksViews() {
    const type = this.taskType;
    let viewType = {
      name: TaskManagerView[this.viewType],
      id: this.viewType,
    };
    if (this.swimLanesUpdate) {
      viewType = {
        name: TaskManagerView[2],
        id: 2,
      };
    }
    this.changeTaskType.emit({
      type,
      viewType,
      duration: this.selectedDuration,
    });
    this.getSwimLanes(this.taskType === 0 ? this.taskTypeSales : this.taskType);
  }

  updateDefaultView() {
    this._loaderService.setLoaderState(true);

    // Save duration to DB instead of localStorage
    const payload: GetTaskDefaultViewSettingDto = {
      viewType: this.viewType,
      durationType: undefined,
    };

    if (this.viewType === TaskManagerView.Calendar) {
      payload.durationType = this.selectedDuration;
    }

    this._taskManagementService
      .updateDefaultViewSetting(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.data != null) {
            this._loaderService.setLoaderState(false);
            this._toastr.success("Success", "Setting updated successfully!");
            this.getDefaultView();
            this.hide.emit();
          } else {
            this._toastr.error("Error", res.message || "Error adding client");
          }
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message || "Error adding client");
        },
      });
  }

  dragStart(task: GetTaskSwimLanesDto, index: number) {
    this.draggedStatus = task;
    this.draggedIndex = index;
  }

  drop(targetIndex: number) {
    if (this.draggedStatus) {
      this.swimLanes.splice(this.draggedIndex, 1);
      this.swimLanes.splice(targetIndex, 0, this.draggedStatus);

      this.restSwimLanes = [];
      this.swimLanes.forEach(item => {
        this.restSwimLanes.push({
          statusId: item.status,
          isActive: item.isActive,
        });
      });

      this.createOrUpdateSwimLanes();

      this.draggedStatus = null;
      this.draggedIndex = 0;
    }
  }

  updateSwimLanes() {
    this.restSwimLanes = [];
    this.restSwimLanes = this.swimLanes.map(item => ({
      isActive: item.isActive ? true : false,
      statusId: item.status,
    }));

    this.swimLanesUpdate = true;

    this.createOrUpdateSwimLanes();
    this.getDefaultView();
    this.hide.emit();
  }

  close() {
    this.hide.emit();
  }
}
